import React, { useCallback, useEffect, useRef, useState } from 'react';
import { loadPecuariaEvents, PECUARIA_CHANGED, resolvePecuariaPhysicalPlot } from '../services/pecuariaService';
import { formatNumber, formatTime } from './pecuariaDomain';
import './Pecuaria.css';

const LABELS = { entrada: 'Entrada no piquete', saida: 'Saída do piquete', medicao: 'Medição de pastagem', observacao: 'Observação de campo',
  lote_criado: 'Novo lote', lote_atualizado: 'Composição atualizada', lote_encerrado: 'Lote encerrado', pastagem_configurada: 'Pastagem configurada', pastagem_encerrada: 'Pastagem encerrada' };
export default function PecuariaHistory({ propertyId = null, plotId = null, legacyPlotId = null, title = 'Histórico pecuário', onNavigate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [more, setMore] = useState(false);
  const [revision, setRevision] = useState(0);
  const before = useRef(null);
  const generation = useRef(0);
  const busy = useRef(false);
  const load = useCallback(async (offset = 0) => {
    const request = ++generation.current;
    busy.current = true; setLoading(true); setError('');
    if (offset === 0) { before.current = new Date().toISOString(); setRows([]); }
    try {
      const physicalId = plotId ?? (legacyPlotId != null ? await resolvePecuariaPhysicalPlot(propertyId, legacyPlotId) : null);
      const data = await loadPecuariaEvents({ propertyId, plotId: physicalId, before: before.current, offset });
      if (generation.current !== request) return;
      setRows(old => offset ? [...old, ...data] : data); setMore(data.length === 30);
    } catch (e) { if (generation.current === request) setError(e.message); }
    finally { if (generation.current === request) { setLoading(false); busy.current = false; } }
  }, [propertyId, plotId, legacyPlotId]);
  useEffect(() => { load(); return () => { generation.current += 1; }; }, [load, revision]);
  useEffect(() => {
    const changed = () => setRevision(v => v + 1);
    window.addEventListener(PECUARIA_CHANGED, changed);
    return () => window.removeEventListener(PECUARIA_CHANGED, changed);
  }, []);
  return <section className="pec-history" aria-label={title}>
    <header><div><span className="pec-kicker">Caderno de Campo · Pecuária</span><h2>{title}</h2></div>
      <button type="button" onClick={() => load()} disabled={loading}>Atualizar histórico</button></header>
    <p>Entradas, saídas, medições e observações com data e autoria preservadas. Nenhuma saída encerra um plantio agrícola.</p>
    {error && <p className="pec-error" role="alert">{error}</p>}
    {loading && <p role="status">Carregando registros pecuários…</p>}
    {!loading && !error && !rows.length && <div className="pec-empty"><strong>Nenhum registro pecuário neste contexto</strong><p>As movimentações e medições realizadas em Pecuária aparecerão aqui.</p>
      {onNavigate && <button type="button" onClick={() => onNavigate('pecuaria', { propertyId, plotId })}>Abrir Pecuária</button>}</div>}
    <ol className="pec-timeline">{rows.map(row => <li key={row.id}>
      <span className="pec-event-tag">{LABELS[row.tipo] || 'Registro pecuário'}</span><time dateTime={row.ocorrido_em}>{formatTime(row.ocorrido_em)}</time>
      <strong>{row.descricao}</strong><span>{[row.propriedade_nome, row.talhao_nome, row.lote_nome].filter(Boolean).join(' · ')}</span>
      {row.dados?.ocupacao && <small>{formatNumber(row.dados.ocupacao.quantidade, 0)} animais · {formatNumber(row.dados.ocupacao.peso_medio_kg)} kg médios · {formatNumber(row.dados.ocupacao.area_util)} ha úteis na entrada</small>}
      {row.tipo === 'medicao' && <small>Altura: {formatNumber(row.dados.altura_cm)} cm · condição: {row.dados.condicao} · método: {row.dados.metodo}</small>}
      {row.dados?.observacoes && <small>{row.dados.observacoes}</small>}
      <small>Por {row.autor_nome || 'usuário autenticado'} · registrado em {formatTime(row.criado_em)}</small>
    </li>)}</ol>
    {more && !error && <button type="button" disabled={loading} onClick={() => { if (!busy.current) load(rows.length); }}>Carregar mais registros</button>}
  </section>;
}
